import { Button, type ButtonProps, Tooltip } from '@sgme/ui';
import type { ProductSelectorTabType } from 'components/shared/ProductAndClientSelector/productAndClientSelector.model';
import type { AnalyticCategoryActions, CommonWorkspaceProductSelectorAction } from 'models';
import type { ProductVariation } from 'models/productsReferential';
import { FC, MouseEventHandler, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';

type ProductQuickStartButtonProps = {
  workspace: WorkspaceWithProductSelector;
  tabType: ProductSelectorTabType;
  selectedProductVariation: ProductVariation;
} & Omit<ButtonProps, 'children' | 'iconPosition' | 'purpose'>;

export const ProductQuickStartButton: FC<ProductQuickStartButtonProps> = ({
  workspace,
  tabType,
  selectedProductVariation,
  onClick,
  ...buttonProps
}) => {
  const { formatMessage } = useIntl();
  const click: MouseEventHandler<HTMLButtonElement> = useCallback(
    e => {
      e.stopPropagation();
      onClick?.(e);
      logAnalytics.trackEvent(
        `Primary market product selector ${workspace}` as keyof AnalyticCategoryActions,
        'Click on button',
        'Quick start',
      );
      logAnalytics.trackEvent(
        `Primary market product selector ${workspace}` as keyof AnalyticCategoryActions,
        `Quick start - ${tabType}` as CommonWorkspaceProductSelectorAction,
        `${selectedProductVariation.productType} ${selectedProductVariation.productSubType} ${selectedProductVariation.wrapper}`,
      );
    },
    [
      onClick,
      selectedProductVariation.productSubType,
      selectedProductVariation.productType,
      selectedProductVariation.wrapper,
      tabType,
      workspace,
    ],
  );
  return (
    <Tooltip position="right" contents={formatMessage({ id: 'productSelector.tooltips.quickStart' })}>
      <Button iconPosition="icon-only" purpose="flat" tabIndex={-1} size="sm" onClick={click} {...buttonProps}>
        <em className="icon text-muted">arrow_forward</em>
      </Button>
    </Tooltip>
  );
};
